import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PlayerPreviewSection from '../components/PlayerPreviewSection';
import PlayerProfilePreviewTabs from '../components/PlayerProfilePreviewTabs';
import PreviewMode from '../components/website-builder/PreviewMode';
// Footer is now rendered by ProfileSiteLayout (unified across all routes)
import { getResolvedTheme } from '../components/website-builder/themeDefaults';
import { getPlayerProfile, getProfileIdFromUsername } from '../services/firestoreService';
import NotFound from './NotFound';
import { useAuth } from '../contexts/AuthContext';
import { isSubdomain, getSubdomainUsername, getCanonicalUrl } from '../utils/subdomainUtils';
import { customDomains } from '../config/customDomains';
import { trackEvent } from '../utils/analytics';

const PlayerProfilePreview = ({ profileData: propProfileData, username: propUsername, openDonationOnMount = false, hideHeader = false }) => {
  const [activeTab, setActiveTab] = useState('basicInfo');
  const [profileData, setProfileData] = useState(propProfileData || null);
  const [loading, setLoading] = useState(!propProfileData);
  const [error, setError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');
  const { username: paramUsername, pageSlug } = useParams();
  const { currentUser } = useAuth();

  // Derive layout theme directly from profileData — no separate state avoids a
  // one-frame flash of the wrong layout between data load and useEffect sync.
  const layoutTheme = profileData?.theme?.layout || 'linkblock';

  // Determine username from subdomain, prop, or URL param (in that order)
  const subdomainUsername = isSubdomain() ? getSubdomainUsername() : null;
  const username = subdomainUsername || propUsername || paramUsername;

  // Detect if we're on a custom domain or subdomain for this profile
  const isOnOwnDomain = (() => {
    const hostname = window.location.hostname.replace(/^www\./, '');
    if (customDomains[hostname]) return true;
    if (isSubdomain() && getSubdomainUsername()?.toLowerCase() === username?.toLowerCase()) return true;
    return false;
  })();

  const handleCopyLink = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopyFeedback('Link copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  };
  
  useEffect(() => {
    // If profile data is already provided as props, skip loading
    if (propProfileData) {
      setLoading(false);
      return;
    }
    
    const loadProfile = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        // Get profile ID from username
        const userInfo = await getProfileIdFromUsername(username);
        
        if (!userInfo) {
          setError('Profile not found');
          setLoading(false);
          return;
        }

        // Load the profile data
        const profileData = await getPlayerProfile(userInfo.profileId);
        
        if (profileData) {
          // Only show published profiles
          if (profileData.status === 'published') {
            setProfileData(profileData);
          } else {
            setError('Profile not found or not published');
          }
        } else {
          setError('Profile not found');
        }
      } catch (err) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [username, propProfileData]);

  // Premium gate: Redirect users accessing via subdomain unless they have premium OR lifetime subdomain
  useEffect(() => {
    // Only check after profile data is loaded
    if (!profileData || loading) {
      return;
    }

    // Allow subdomain access if user has premium OR earned lifetime subdomain via referrals
    const canUseSubdomain = profileData.playerPremium || profileData.hasLifetimeSubdomain;

    // If accessed via subdomain but user doesn't have access
    if (isSubdomain() && !canUseSubdomain) {
      // Redirect to /me browse/sales page (not their profile)
      window.location.href = 'https://chessweb.site/me';
    }
  }, [profileData, loading]);

  // Track Profile View event
  useEffect(() => {
    if (!profileData || loading) return;
    trackEvent('Profile View', {
      coach: username,
      source: isSubdomain() ? 'subdomain' : 'direct-path',
    });

  }, [profileData, loading, username]);

  // Loading state
  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-6 overflow-x-hidden"
        style={{
          background: `
            linear-gradient(45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%), 
            linear-gradient(-45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%), 
            linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%), 
            linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%),
            linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 90%)
          `,
          boxShadow: 'inset 0 2px 12px rgba(12, 10, 10, 0.6)'
        }}
      >
        <div className="flex flex-col items-center space-y-6 text-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center animate-pulse">
              <i className="fas fa-chess-king text-white/30 text-3xl"></i>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <i className="fas fa-chess-king text-white/50 text-3xl animate-bounce"></i>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !profileData) {
    return <NotFound />;
  }

  // Resolve theme for page-level styling (custom layout backgrounds)
  const resolvedTheme = getResolvedTheme(profileData?.theme || {});
  const isLightTheme = resolvedTheme.preset === 'light';
  const pageBg = resolvedTheme.colors.pageBackground;

  // Helper function to generate player description for meta tags
  const generatePlayerDescription = () => {
    const rating = profileData.fideRating || profileData.nationalRating || '';
    const federation = profileData.nationalFederation || '';
    const bio = profileData.biography ? profileData.biography.substring(0, 150) + '...' : '';
    
    if (rating && federation) {
      return `Chess player rated ${rating} from ${federation}. ${bio}`.trim();
    } else if (bio) {
      return bio;
    } else {
      return `${profileData.displayName} - Chess player profile on ChessWeb.site`;
    }
  };

  return (
    <>
      {/* Dynamic Meta Tags for Player Profile */}
      <Helmet>
        <title>{pageSlug && profileData.pages?.[pageSlug] ? `${profileData.pages[pageSlug].title || pageSlug} | ${profileData.displayName} | ChessWeb.site` : `${profileData.displayName} | ChessWeb.site`}</title>
        <meta name="title" content={pageSlug && profileData.pages?.[pageSlug] ? `${profileData.pages[pageSlug].title || pageSlug} | ${profileData.displayName} | ChessWeb.site` : `${profileData.displayName} | ChessWeb.site`} />
        <meta name="description" content={generatePlayerDescription()} />

        {/* Canonical URL - Important for SEO when accessed via subdomain */}
        <link rel="canonical" href={getCanonicalUrl()} />

        {/* Preload critical profile photo for faster LCP */}
        {profileData.photoUrl && (
          <link rel="preload" as="image" href={profileData.photoUrl} />
        )}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`https://chessweb.site/me/${username}`} />
        <meta property="og:title" content={`${profileData.displayName} | ChessWeb.site`} />
        <meta property="og:description" content={generatePlayerDescription()} />
        <meta property="og:image" content={profileData.socialShareImage || 'https://chessweb.site/SocialSharing.png'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://chessweb.site/me/${username}`} />
        <meta name="twitter:title" content={`${profileData.displayName} | ChessWeb.site`} />
        <meta name="twitter:description" content={generatePlayerDescription()} />
        <meta name="twitter:image" content={profileData.socialShareImage || 'https://chessweb.site/SocialSharing.png'} />

        {/* JSON-LD Person Schema */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: profileData.displayName,
          url: `https://chessweb.site/me/${username}`,
          ...(profileData.photoUrl && { image: profileData.photoUrl }),
          ...(profileData.biography && { description: profileData.biography.substring(0, 300) }),
          ...(profileData.fideTitle && { jobTitle: `${profileData.fideTitle} Chess Player` }),
          ...(profileData.location && { address: { '@type': 'PostalAddress', addressLocality: profileData.location } }),
          ...(profileData.nationalFederation && { affiliation: { '@type': 'Organization', name: profileData.nationalFederation } }),
          ...(profileData.fideId && { sameAs: [`https://ratings.fide.com/profile/${profileData.fideId}`] })
        })}</script>
      </Helmet>


      {layoutTheme === 'custom' ? (
        /* Custom layout — no fixed background or min-h-screen needed.
           ProfileSiteLayout handles background, header, and footer. */
        <div>
          {pageSlug && profileData.pages?.[pageSlug] ? (
            <PreviewMode
              blocks={profileData.pages[pageSlug].blocks || []}
              youtubeVideos={profileData.youtubeVideos || []}
              photos={profileData.photos || []}
              profileId={profileData.profileId || profileData.id}
              profileName={profileData.headerName || profileData.displayName || profileData.name || 'Profile'}
              profileLogo={profileData.photoUrl || profileData.avatar || profileData.logo || null}
              username={username}
              headerNavLinks={profileData.headerNavLinks || []}
              headerNavStyle={profileData.headerNavStyle || 'buttons'}
              headerStyle={profileData.headerStyle || {}}
              accentColor={profileData.primaryColorHex || '#749555'}
              theme={profileData?.theme}
              hideFooter
              hideHeader={hideHeader}
              announcement={profileData.announcement}
              isOnOwnDomain={isOnOwnDomain}
            />
          ) : pageSlug && !profileData.pages?.[pageSlug] ? (
            <NotFound />
          ) : profileData.customBlocks?.length > 0 ? (
            <PreviewMode
              blocks={profileData.customBlocks}
              youtubeVideos={profileData.youtubeVideos || []}
              photos={profileData.photos || []}
              profileId={profileData.profileId || profileData.id}
              profileName={profileData.headerName || profileData.displayName || profileData.name || 'Profile'}
              profileLogo={profileData.photoUrl || profileData.avatar || profileData.logo || null}
              username={username}
              headerNavLinks={profileData.headerNavLinks || []}
              headerNavStyle={profileData.headerNavStyle || 'buttons'}
              headerStyle={profileData.headerStyle || {}}
              accentColor={profileData.primaryColorHex || '#749555'}
              theme={profileData?.theme}
              hideFooter
              hideHeader={hideHeader}
              announcement={profileData.announcement}
              isOnOwnDomain={isOnOwnDomain}
            />
          ) : (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fa-solid fa-hammer text-3xl text-gray-500"></i>
                </div>
                <h1 className="text-2xl font-bold text-white mb-3">
                  {profileData?.displayName || 'This user'} is setting up shop.
                </h1>
                <p className="text-gray-400 mb-6">
                  Check back soon!
                </p>
                <a
                  href="https://chessweb.site"
                  className="inline-flex items-center gap-2 bg-chess-green hover:bg-chess-green/90 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  <i className="fa-solid fa-chess-knight"></i>
                  Visit ChessWeb.site
                </a>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Non-custom layouts (linkblock, tabs) — keep fixed background + own footer */
        <div className="relative min-h-screen" style={{ backgroundColor: '#1a1a1a', overscrollBehaviorY: 'contain' }}>
          <div
            className="fixed inset-0 w-full h-screen z-0"
            style={{
              background: `
                linear-gradient(45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%),
                linear-gradient(-45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%),
                linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%),
                linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 90%)
              `,
              backgroundSize: '100% 100%',
              backgroundAttachment: 'fixed',
              boxShadow: 'inset 0 2px 12px rgba(12, 10, 10, 0.6)'
            }}
          />
          <div className="relative z-10 min-h-screen flex flex-col" style={{ overscrollBehaviorY: 'contain' }}>
            <div className="flex-grow">
              {layoutTheme === 'tabs' ? (
                <PlayerProfilePreviewTabs
                  profileData={profileData}
                  toggles={profileData.sections || {}}
                  isPublicView={true}
                  profileId={profileData.profileId || profileData.id}
                  openDonationOnMount={openDonationOnMount}
                />
              ) : (
                <PlayerPreviewSection
                  profileData={profileData}
                  toggles={profileData.sections || {}}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isPublicView={true}
                  profileId={profileData.profileId || profileData.id}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/** Returns true if a hex color is "light" (better with dark text) */
const isLightColor = (hex) => {
  if (!hex || !hex.startsWith('#')) return false;
  const num = parseInt(hex.slice(1), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

const FooterCta = ({ profile, currentUser }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://chessweb.site';
  const profileUsername = profile?.username || profile?.slug || '';
  const isOwner = Boolean(currentUser?.uid && (profile?.userId === currentUser.uid || profile?.ownerId === currentUser.uid));

  const accentColor = profile?.primaryColorHex || profile?.theme?.primaryColor || '#749555';
  const light = isLightColor(accentColor);
  const textColor = light ? '#111827' : '#ffffff';

  // Authenticated user viewing their own profile
  if (isOwner && profileUsername) {
    return (
      <div className="text-center space-y-4" style={{ color: textColor }}>
        <p className="mb-2">
          Logged in as{' '}
          <span className="font-semibold">@{profileUsername}</span>
        </p>
        <a
          href={`${origin}/dashboard`}
          className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-lg hover:opacity-90 transition-colors"
          style={{
            backgroundColor: light ? '#111827' : '#ffffff',
            color: light ? '#ffffff' : '#111827',
          }}
        >
          View Dashboard
          <i className="fas fa-arrow-right ml-2"></i>
        </a>
      </div>
    );
  }

  // Non-authenticated visitor (default)
  const referralEnabled = profile?.referral?.enabled !== false;
  const referralId = profile?.userId || profile?.id;
  const loginUrl = referralEnabled && referralId
    ? `/auth?mode=login&context=creator&ref=${referralId}`
    : `/auth?mode=login&context=creator`;

  return (
    <div className="text-center space-y-3" style={{ color: textColor }}>
      <a
        href={loginUrl}
        className="inline-block px-6 py-3 font-semibold rounded-lg hover:opacity-90 transition-colors"
        style={{
          backgroundColor: light ? '#111827' : '#ffffff',
          color: light ? '#ffffff' : '#111827',
        }}
      >
        Log in to ChessWeb.site
      </a>
    </div>
  );
};

export default PlayerProfilePreview;
